import { useEffect, useState, useRef } from 'react';
import { useOutletContext } from 'react-router-dom';
import { 
  getLibrarySections, 
  getLibraryCalls, 
  searchLibrary, 
  logLibraryAccess 
} from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { 
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { 
  Library,
  Search,
  Play,
  Pause,
  Star,
  Clock,
  User,
  Phone,
  Filter,
  Users,
  RefreshCw,
  Heart,
  Shield,
  Target,
  MessageSquare,
  Folder,
  Sparkles,
  ChevronRight,
  Volume2,
  Award,
  TrendingUp,
  Eye,
  Headphones,
  Loader2
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';

const ICON_MAP = {
  Phone: Phone,
  Filter: Filter,
  Users: Users,
  RefreshCw: RefreshCw,
  Heart: Heart,
  Shield: Shield,
  Target: Target,
  MessageSquare: MessageSquare,
  Folder: Folder,
};

export default function UserLibrary() {
  const { user } = useOutletContext();
  const [sections, setSections] = useState([]);
  const [calls, setCalls] = useState([]);
  const [totalCalls, setTotalCalls] = useState(0);
  const [selectedSection, setSelectedSection] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  
  // Search
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [isSearching, setIsSearching] = useState(false);
  const [searchTotal, setSearchTotal] = useState(0);
  
  // Call detail dialog
  const [selectedCall, setSelectedCall] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const audioRef = useRef(null);

  const fetchSections = async () => {
    try {
      const { data } = await getLibrarySections();
      setSections(data);
    } catch (error) {
      console.error('Failed to fetch sections:', error);
    }
  };

  const fetchCalls = async (sectionId = null, skip = 0, append = false) => {
    if (skip === 0) setIsLoading(true);
    else setIsLoadingMore(true);
    
    try {
      const { data } = await getLibraryCalls(sectionId, 12, skip);
      if (append) {
        setCalls(prev => [...prev, ...data.calls]);
      } else {
        setCalls(data.calls);
      }
      setTotalCalls(data.total);
    } catch (error) {
      console.error('Failed to fetch library calls:', error);
    } finally {
      setIsLoading(false);
      setIsLoadingMore(false);
    }
  };

  useEffect(() => {
    fetchSections();
    fetchCalls();
  }, []);

  const handleSectionChange = (sectionId) => {
    setSelectedSection(sectionId);
    setSearchResults(null);
    setSearchQuery('');
    fetchCalls(sectionId === 'all' ? null : sectionId);
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      setSearchResults(null);
      return;
    }
    
    setIsSearching(true);
    try {
      const { data } = await searchLibrary(searchQuery, user?.id, 10, 0);
      setSearchResults(data.calls);
      setSearchTotal(data.total);
      toast.success(`Found ${data.total} matching calls`);
    } catch (error) {
      toast.error('Search failed');
    } finally {
      setIsSearching(false);
    }
  };

  const handleLoadMoreSearch = async () => {
    if (!searchQuery.trim()) return;
    
    setIsLoadingMore(true);
    try {
      const { data } = await searchLibrary(searchQuery, user?.id, 10, searchResults.length);
      setSearchResults(prev => [...prev, ...data.calls]);
    } catch (error) {
      toast.error('Failed to load more');
    } finally {
      setIsLoadingMore(false);
    }
  };

  const handleLoadMore = () => {
    fetchCalls(selectedSection === 'all' ? null : selectedSection, calls.length, true);
  };

  const handleViewCall = async (libraryCall) => {
    setSelectedCall(libraryCall);
    if (user?.id && libraryCall.id) {
      try {
        await logLibraryAccess(libraryCall.id, user.id, 'view');
      } catch (error) {
        console.error('Failed to log access:', error);
      }
    }
  };

  const handlePlayAudio = async () => {
    if (!audioRef.current) return;
    
    if (isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
    } else {
      audioRef.current.play();
      setIsPlaying(true);
      
      // Log listen action
      if (user?.id && selectedCall?.id) {
        try {
          await logLibraryAccess(selectedCall.id, user.id, 'listen');
        } catch (error) {
          console.error('Failed to log listen:', error);
        }
      }
    }
  };

  const getSectionIcon = (iconName) => {
    const IconComponent = ICON_MAP[iconName] || Folder;
    return <IconComponent className="h-5 w-5" />;
  };

  const displayCalls = searchResults || calls;

  if (isLoading && !calls.length) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-32" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[...Array(6)].map((_, i) => <Skeleton key={i} className="h-64" />)}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="user-library">
      {/* Header */}
      <div className="flex items-center gap-4 p-6 bg-gradient-to-r from-amber-500 to-orange-500 rounded-xl text-white shadow-lg">
        <div className="p-3 bg-card/20 rounded-xl">
          <Library className="h-8 w-8" />
        </div>
        <div className="flex-1">
          <h1 className="text-2xl font-bold font-heading">Call Library</h1>
          <p className="text-amber-100">Learn from the best calls • {totalCalls} calls available</p>
        </div>
      </div>

      {/* Search Bar */}
      <Card className="card-elevated">
        <CardContent className="p-4">
          <div className="flex gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by situation, topic, or skill (e.g., 'handling salary negotiation', 'objection about relocation')..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                className="pl-10"
              />
            </div>
            <Button onClick={handleSearch} disabled={isSearching}>
              {isSearching ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Search className="h-4 w-4" />
              )}
              <span className="ml-2">Search</span>
            </Button>
            {searchResults && (
              <Button variant="outline" onClick={() => { setSearchResults(null); setSearchQuery(''); }}>
                Clear
              </Button>
            )}
          </div>
          {searchResults && (
            <p className="text-sm text-muted-foreground mt-2">
              Found {searchTotal} calls matching &quot;{searchQuery}&quot;
            </p>
          )}
        </CardContent>
      </Card>

      {/* Sections */}
      {!searchResults && (
        <div className="flex gap-2 flex-wrap">
          <Button
            variant={selectedSection === null ? 'default' : 'outline'}
            size="sm"
            onClick={() => handleSectionChange(null)}
          >
            <Sparkles className="h-4 w-4 mr-1" />
            All Best Calls
          </Button>
          {sections.map((section) => (
            <Button
              key={section.id}
              variant={selectedSection === section.id ? 'default' : 'outline'}
              size="sm"
              onClick={() => handleSectionChange(section.id)}
            >
              {getSectionIcon(section.icon)}
              <span className="ml-1">{section.name}</span>
            </Button>
          ))}
        </div>
      )}

      {/* Call Gallery */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {displayCalls.map((libraryCall) => (
          <Card 
            key={libraryCall.id || libraryCall.call?.id} 
            className={`card-elevated hover:shadow-lg transition-all cursor-pointer group ${libraryCall.is_featured ? 'ring-2 ring-amber-400' : ''}`}
            onClick={() => handleViewCall(libraryCall)}
          >
            <CardContent className="p-0">
              {/* Top Banner */}
              <div className={`p-4 ${libraryCall.is_featured ? 'bg-gradient-to-r from-amber-100 to-orange-100' : 'bg-gradient-to-r from-slate-50 to-slate-100'}`}>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    {libraryCall.is_featured && (
                      <Badge className="bg-amber-500 text-white">
                        <Star className="h-3 w-3 mr-1" />
                        Featured
                      </Badge>
                    )}
                    <Badge variant="outline" className="capitalize">
                      {libraryCall.call?.call_type || 'call'}
                    </Badge>
                  </div>
                  <div className="flex items-center gap-1 text-sm text-muted-foreground">
                    <Eye className="h-4 w-4" />
                    {libraryCall.view_count || 0}
                    <Headphones className="h-4 w-4 ml-2" />
                    {libraryCall.listen_count || 0}
                  </div>
                </div>
                
                <div className="flex items-center gap-3">
                  <div className="p-3 bg-card rounded-full shadow-sm">
                    <Volume2 className="h-6 w-6 text-amber-600" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-foreground group-hover:text-amber-600 transition-colors">
                      {libraryCall.call?.candidate_name || 'Call Recording'}
                    </h3>
                    <p className="text-sm text-muted-foreground">by {libraryCall.user_name}</p>
                  </div>
                </div>
              </div>
              
              {/* Content */}
              <div className="p-4 space-y-3">
                {/* Score */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Award className="h-5 w-5 text-amber-500" />
                    <span className="text-2xl font-bold text-foreground">
                      {libraryCall.call?.average_score?.toFixed(1) || '-'}
                    </span>
                    <span className="text-muted-foreground">/10</span>
                  </div>
                  <Badge className={
                    libraryCall.call?.classification === 'Best' ? 'bg-emerald-100 text-emerald-700' :
                    libraryCall.call?.classification === 'Good' ? 'bg-blue-100 text-blue-700' :
                    'bg-muted text-foreground'
                  }>
                    {libraryCall.call?.classification || 'Quality'}
                  </Badge>
                </div>
                
                {/* Highlight Skills */}
                {libraryCall.highlight_skills?.length > 0 && (
                  <div className="flex flex-wrap gap-1">
                    {libraryCall.highlight_skills.slice(0, 3).map((skill, idx) => (
                      <Badge key={idx} variant="outline" className="text-xs">
                        {skill}
                      </Badge>
                    ))}
                  </div>
                )}
                
                {/* Search Snippet */}
                {libraryCall.relevance_snippet && (
                  <p className="text-sm text-muted-foreground bg-amber-50 p-2 rounded border border-amber-100 line-clamp-2">
                    &quot;{libraryCall.relevance_snippet}&quot;
                  </p>
                )}
                
                {/* Meta */}
                <div className="flex items-center gap-4 text-xs text-muted-foreground pt-2 border-t">
                  <span className="flex items-center gap-1">
                    <Clock className="h-3 w-3" />
                    {libraryCall.call?.duration ? `${Math.round(libraryCall.call.duration / 60)} min` : '-'}
                  </span>
                  <span className="flex items-center gap-1">
                    <Phone className="h-3 w-3" />
                    {libraryCall.call?.phone_number || '-'}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Load More */}
      {!searchResults && calls.length < totalCalls && (
        <div className="text-center">
          <Button variant="outline" onClick={handleLoadMore} disabled={isLoadingMore}>
            {isLoadingMore ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <ChevronRight className="h-4 w-4 mr-2" />
            )}
            Load More ({calls.length} of {totalCalls})
          </Button>
        </div>
      )}

      {searchResults && searchResults.length < searchTotal && (
        <div className="text-center">
          <Button variant="outline" onClick={handleLoadMoreSearch} disabled={isLoadingMore}>
            {isLoadingMore ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <ChevronRight className="h-4 w-4 mr-2" />
            )}
            Load More ({searchResults.length} of {searchTotal})
          </Button>
        </div>
      )}

      {/* Empty State */}
      {displayCalls.length === 0 && !isLoading && (
        <Card className="card-elevated">
          <CardContent className="py-12 text-center">
            <Library className="h-12 w-12 text-muted-foreground/70 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-foreground">No Calls Found</h3>
            <p className="text-muted-foreground">
              {searchResults ? 'Try a different search query' : 'No calls in this section yet'}
            </p>
          </CardContent>
        </Card>
      )}

      {/* Call Detail Dialog */}
      <Dialog open={!!selectedCall} onOpenChange={() => { setSelectedCall(null); setIsPlaying(false); }}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Volume2 className="h-5 w-5 text-amber-500" />
              {selectedCall?.call?.candidate_name || 'Call Recording'}
            </DialogTitle>
            <DialogDescription>
              by {selectedCall?.user_name} • {selectedCall?.call?.call_type}
            </DialogDescription>
          </DialogHeader>
          
          {selectedCall && (
            <div className="space-y-6">
              {/* Audio Player */}
              {selectedCall.call?.audio_url && (
                <div className="p-4 bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl">
                  <div className="flex items-center gap-4">
                    <Button
                      size="lg"
                      className="rounded-full h-14 w-14 bg-amber-500 hover:bg-amber-600"
                      onClick={handlePlayAudio}
                    >
                      {isPlaying ? <Pause className="h-6 w-6" /> : <Play className="h-6 w-6" />}
                    </Button>
                    <div className="flex-1">
                      <audio
                        ref={audioRef}
                        src={selectedCall.call.audio_url}
                        onEnded={() => setIsPlaying(false)}
                        controls
                        className="w-full"
                      />
                    </div>
                  </div>
                </div>
              )}
              
              {/* Score & Classification */}
              <div className="grid grid-cols-3 gap-4">
                <div className="p-4 bg-amber-50 rounded-lg text-center">
                  <p className="text-3xl font-bold text-amber-600">{selectedCall.call?.average_score?.toFixed(1) || '-'}</p>
                  <p className="text-sm text-amber-700">Score</p>
                </div>
                <div className="p-4 bg-emerald-50 rounded-lg text-center">
                  <p className="text-xl font-semibold text-emerald-600">{selectedCall.call?.classification || '-'}</p>
                  <p className="text-sm text-emerald-700">Classification</p>
                </div>
                <div className="p-4 bg-blue-50 rounded-lg text-center">
                  <p className="text-xl font-semibold text-blue-600">{selectedCall.call?.duration ? `${Math.round(selectedCall.call.duration / 60)}m` : '-'}</p>
                  <p className="text-sm text-blue-700">Duration</p>
                </div>
              </div>
              
              {/* Summary */}
              {selectedCall.call?.summary && (
                <div>
                  <h4 className="font-semibold text-foreground mb-2">Call Summary</h4>
                  <p className="text-sm text-muted-foreground bg-muted/50 p-3 rounded-lg">{selectedCall.call.summary}</p>
                </div>
              )}
              
              {/* Strengths */}
              {selectedCall.call?.strengths?.length > 0 && (
                <div>
                  <h4 className="font-semibold text-foreground mb-2 flex items-center gap-2">
                    <TrendingUp className="h-4 w-4 text-emerald-500" />
                    Strengths Demonstrated
                  </h4>
                  <div className="space-y-2">
                    {selectedCall.call.strengths.map((strength, idx) => (
                      <div key={idx} className="flex items-start gap-2 p-2 bg-emerald-50 rounded-lg">
                        <Star className="h-4 w-4 text-emerald-500 mt-0.5 shrink-0" />
                        <p className="text-sm text-foreground">{strength}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Coaching Tips */}
              {selectedCall.call?.coaching_tips?.length > 0 && (
                <div>
                  <h4 className="font-semibold text-foreground mb-2 flex items-center gap-2">
                    <MessageSquare className="h-4 w-4 text-blue-500" />
                    What Made This Call Great
                  </h4>
                  <div className="space-y-2">
                    {selectedCall.call.coaching_tips.slice(0, 5).map((tip, idx) => (
                      <div key={idx} className="flex items-start gap-2 p-2 bg-blue-50 rounded-lg">
                        <Sparkles className="h-4 w-4 text-blue-500 mt-0.5 shrink-0" />
                        <p className="text-sm text-foreground">{tip}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Score Breakdown */}
              {(selectedCall.call?.score_breakdown || selectedCall.call?.quality_scores) && (
                <div>
                  <h4 className="font-semibold text-foreground mb-2">Score Breakdown</h4>
                  <div className="grid grid-cols-2 gap-2">
                    {Object.entries(selectedCall.call.score_breakdown || selectedCall.call.quality_scores || {}).map(([key, value]) => (
                      <div key={key} className="flex items-center justify-between p-2 bg-muted/50 rounded">
                        <span className="text-sm text-muted-foreground capitalize">{key.replace(/_/g, ' ')}</span>
                        <span className={`font-semibold ${value >= 9 ? 'text-emerald-600' : value >= 7 ? 'text-blue-600' : 'text-muted-foreground'}`}>
                          {value}/10
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Transcription Preview */}
              {selectedCall.call?.transcript && (
                <div>
                  <h4 className="font-semibold text-foreground mb-2">Transcription Preview</h4>
                  <ScrollArea className="h-40 w-full rounded-lg border bg-muted/50 p-3">
                    <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                      {selectedCall.call.transcript}
                    </p>
                  </ScrollArea>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
